import {
    registriesAPI,
    RegistryEntry,
    SavedRegistrySchema,
} from "@/services/registries/registriesAPI";
import { ref, watch } from "vue";
import { withLoadingScreen } from "@/lib/loading";
import {
    ExperimentSetupRelationshipConfig,
    SavedExperimentSetupRelationshipConfig,
} from "@/services/experimentRelationships/models";

function findRegistrySchemaById(allRegistrySchemas: SavedRegistrySchema[], id: string) {
    return allRegistrySchemas.find((registrySchema) => registrySchema.id === id);
}

export function withRelationshipConfigForm(
    allRegistrySchemas: SavedRegistrySchema[],
    type: string,
    existingRelationshipConfig?: SavedExperimentSetupRelationshipConfig
) {
    const selectedRegistrySchema = ref<SavedRegistrySchema | undefined>(
        existingRelationshipConfig
            ? findRegistrySchemaById(
                  allRegistrySchemas,
                  existingRelationshipConfig.registrySchemaId
              )
            : undefined
    );
    const required = ref<boolean>(existingRelationshipConfig?.required || false);
    const allowMultiple = ref<boolean>(existingRelationshipConfig?.allowMultiple || false);
    const registryEntries = ref<RegistryEntry[]>([]);

    const defaultRelationshipRegistryEntryIds = ref<string[]>(
        existingRelationshipConfig?.defaultRegistryEntryIds || []
    );

    const defaultRelationshipRegistryEntryId = ref<string | undefined>(
        existingRelationshipConfig &&
            existingRelationshipConfig.defaultRegistryEntryIds &&
            existingRelationshipConfig.defaultRegistryEntryIds.length > 0
            ? existingRelationshipConfig?.defaultRegistryEntryIds[0]
            : undefined
    );

    watch(selectedRegistrySchema, async () => {
        defaultRelationshipRegistryEntryIds.value = [];
        defaultRelationshipRegistryEntryId.value = undefined;
        if (selectedRegistrySchema.value) {
            const name = selectedRegistrySchema.value.name;
            await withLoadingScreen(async () => {
                registryEntries.value = await registriesAPI.fetchRegistryEntriesForSchemaName(name);
            });
        } else {
            registryEntries.value = [];
        }
    });

    watch(allowMultiple, () => {
        if (allowMultiple.value && defaultRelationshipRegistryEntryId.value) {
            defaultRelationshipRegistryEntryIds.value = [defaultRelationshipRegistryEntryId.value];
        } else if (
            !allowMultiple.value &&
            defaultRelationshipRegistryEntryIds.value &&
            defaultRelationshipRegistryEntryIds.value.length > 0
        ) {
            defaultRelationshipRegistryEntryId.value = defaultRelationshipRegistryEntryIds.value[0];
        }
    });

    function buildBaseRelationshipConfig(): ExperimentSetupRelationshipConfig {
        const defaultRegistryEntryIds: string[] = allowMultiple.value
            ? [...defaultRelationshipRegistryEntryIds.value]
            : [defaultRelationshipRegistryEntryId.value!];

        const baseRelationshipConfig = {
            experimentGroupId: null,
            schemaConfigId: null,
            type: type,
            registrySchemaId: selectedRegistrySchema.value!.id,
            required: required.value,
            allowMultiple: allowMultiple.value,
            defaultRegistryEntryIds,
        };

        if (existingRelationshipConfig) {
            baseRelationshipConfig["id"] = existingRelationshipConfig.id;
        }

        return baseRelationshipConfig;
    }

    async function init() {
        if (existingRelationshipConfig) {
            await withLoadingScreen(async () => {
                const name = selectedRegistrySchema.value!.name;
                registryEntries.value = await registriesAPI.fetchRegistryEntriesForSchemaName(name);
            });
        }
    }

    return {
        selectedRegistrySchema,
        required,
        allowMultiple,
        registryEntries,
        defaultRelationshipRegistryEntryIds,
        defaultRelationshipRegistryEntryId,
        init,
        buildBaseRelationshipConfig,
    };
}
